import React, {
    createContext, useContext, useCallback, useEffect, useRef, useState,
} from "react";
import { api, getUser } from "@/lib/api";

/**
 * RadioProvider — lifts the radio audio engine ABOVE the route tree so the
 * `<audio>` element survives every page navigation (iter 31.14, 23 May 2026).
 *
 * Background: each page wraps itself in `<Shell>`, so navigating from
 * /brief to /ledger remounts Shell, which used to take the radio player
 * (and its <audio>) with it — playback stopped. No. 6 caught it within
 * minutes of the post-31.13 deploy.
 *
 * Architecture:
 *   App.js → <RadioProvider>  ← owns <audio>, state, effects, methods
 *     └─ Routes / Shell / ...
 *         └─ <RadioPlayer />  ← pure UI consumer (button + dropdown)
 *
 * RadioProvider re-renders are cheap (state updates rebroadcast via
 * context) but the underlying <audio> DOM node is stable across the
 * entire session.
 */
const FALLBACK_HOURS_MAX = 2;
// Iter 31.135 — Six's canon: as bulletins grow more frequent (room
// photos + task list will feed the pool), the cadence scales with
// pool size. Formula: every_n_songs = max(2, 6 - floor(pool_size / 5)).
//   pool 0-4  → every 6 songs (unchanged baseline)
//   pool 5-9  → every 5
//   pool 10-14 → every 4
//   pool 15-19 → every 3
//   pool 20+   → every 2 (capped — music stays ≥50% of airtime)
const SONGS_PER_BULLETIN_BASELINE = 6;
const SONGS_PER_BULLETIN_FLOOR = 2;
function songsPerBulletinFor(poolSize) {
    const adj = Math.floor(Math.max(0, poolSize) / 5);
    return Math.max(SONGS_PER_BULLETIN_FLOOR, SONGS_PER_BULLETIN_BASELINE - adj);
}
const TOKEN_KEY = "msd_token";
const COUNTER_KEY = "shed_radio_song_count";
const LAST_BULLETIN_KEY = "shed_radio_last_bulletin";
const LAST_TRACK_ID_KEY = "shed_radio_last_track_id";

const RadioContext = createContext(null);

export function useRadio() {
    const ctx = useContext(RadioContext);
    if (!ctx) {
        throw new Error("useRadio must be called inside <RadioProvider>");
    }
    return ctx;
}

export default function RadioProvider({ children }) {
    const [muted, setMuted] = useState(true);
    const [playing, setPlaying] = useState(false);
    const [tracks, setTracks] = useState([]);
    const [trackIdx, setTrackIdx] = useState(0);
    const [bulletinBusy, setBulletinBusy] = useState(false);
    const [statusLine, setStatusLine] = useState("");
    // Iter 31.135 — pool size from the most recent /radio/bulletin/spy.
    // Drives dynamic SONGS_PER_BULLETIN via songsPerBulletinFor().
    const [bulletinPoolSize, setBulletinPoolSize] = useState(0);
    // Iter 31.136 — performer roster. currentAnnouncer is set during
    // bulletin readout (rotated server-side across the four agents with
    // rendered portraits). currentPerformerKey is derived from the
    // playing track's metadata (gendered cues / lyrics / manual hint).
    const [currentAnnouncer, setCurrentAnnouncer] = useState(null);
    const [streamUrl, setStreamUrl] = useState("");
    const audioRef = useRef(null);
    const streamBlobRef = useRef(null);

    // Track auth state so the provider can lazy-load the library only
    // when a user is signed in. Skips API calls on the public /login
    // and landing surfaces — no point hitting /radio/tracks with no JWT.
    const [authed, setAuthed] = useState(() => Boolean(getUser()));
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === TOKEN_KEY || e.key === "msd_user") {
                setAuthed(Boolean(getUser()));
            }
        };
        window.addEventListener("storage", onStorage);
        // Custom event for same-tab login (storage doesn't fire same-tab)
        const onAuth = () => setAuthed(Boolean(getUser()));
        window.addEventListener("shed:auth-change", onAuth);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("shed:auth-change", onAuth);
        };
    }, []);

    // ---- mute pref ----
    useEffect(() => {
        const stored = localStorage.getItem("shed_radio_muted");
        if (stored !== null) setMuted(stored === "1");
    }, []);
    useEffect(() => {
        localStorage.setItem("shed_radio_muted", muted ? "1" : "0");
        if (audioRef.current) audioRef.current.muted = muted;
    }, [muted]);

    // ---- load library once per signed-in session ----
    useEffect(() => {
        if (!authed) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/radio/tracks");
                if (cancelled) return;
                const trackList = data.tracks || [];
                setTracks(trackList);
                try {
                    const lastId = localStorage.getItem(LAST_TRACK_ID_KEY);
                    if (lastId) {
                        const i = trackList.findIndex((t) => t.id === lastId);
                        if (i >= 0) setTrackIdx(i);
                    }
                } catch (_) { /* non-fatal */ }
            } catch (_) { /* nub fails quiet */ }
        })();
        return () => { cancelled = true; };
    }, [authed]);

    const currentTrack = tracks[trackIdx] || null;
    useEffect(() => {
        if (currentTrack?.id) {
            try { localStorage.setItem(LAST_TRACK_ID_KEY, currentTrack.id); } catch (_) {}
        }
    }, [currentTrack]);

    // ---- stream URL (direct, Range-buffered) ----
    // Iter 31.26 (24 May 2026): switched from blob-download to direct
    // streaming. The backend now accepts JWT via `?t=<token>` query
    // param AND honours HTTP Range requests, so the browser's native
    // <audio> element progressively buffers the file. First-byte to
    // playback drops from "wait for the whole MP3" to "wait for ~64KB".
    useEffect(() => {
        if (streamBlobRef.current) {
            URL.revokeObjectURL(streamBlobRef.current);
            streamBlobRef.current = null;
        }
        if (!currentTrack) {
            setStreamUrl("");
            return;
        }
        const token = localStorage.getItem(TOKEN_KEY) || "";
        const base = process.env.REACT_APP_BACKEND_URL || "";
        // Build URL with token in query string — <audio> can't set headers.
        const url = `${base}/api/radio/tracks/${currentTrack.id}/stream?t=${encodeURIComponent(token)}`;
        setStreamUrl(url);
        setStatusLine(playing ? "//ON AIR" : "Tap ▶ to play");
    }, [currentTrack, playing]);

    // ---- Bulletin ----
    const speak = useCallback((text) => {
        return new Promise((resolve) => {
            if (!text || !("speechSynthesis" in window)) { resolve(); return; }
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(text);
            u.rate = 0.95; u.pitch = 0.9; u.volume = muted ? 0 : 0.9;
            const voices = window.speechSynthesis.getVoices();
            const preferred =
                voices.find((v) => /en-GB/i.test(v.lang) && /male|daniel|oliver/i.test(v.name)) ||
                voices.find((v) => /en-GB/i.test(v.lang)) ||
                voices.find((v) => /en/i.test(v.lang));
            if (preferred) u.voice = preferred;
            u.onend = () => resolve();
            u.onerror = () => resolve();
            window.speechSynthesis.speak(u);
        });
    }, [muted]);

    const playJingle = useCallback((url) => {
        return new Promise((resolve) => {
            if (!url) { resolve(); return; }
            const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${url}`);
            a.volume = muted ? 0 : 0.9;
            a.onended = resolve;
            a.onerror = resolve;
            a.play().catch(() => resolve());
            setTimeout(resolve, 10_000);
        });
    }, [muted]);

    const playBulletin = useCallback(async () => {
        setBulletinBusy(true);
        const a = audioRef.current;
        const prevVol = a ? a.volume : 0.9;
        if (a) a.volume = 0.2;
        try {
            const { data } = await api.get("/radio/bulletin/spy");
            setStatusLine("//BULLETIN");
            if (typeof data?.pool_size === "number") {
                setBulletinPoolSize(data.pool_size);
            }
            // Iter 31.136 — capture which agent is announcing this
            // bulletin so the nub + Now Playing surfaces can swap face.
            if (data?.announcer?.key) {
                setCurrentAnnouncer(data.announcer.key);
            }
            await playJingle(data?.jingle?.url);
            await speak(data?.time?.text);
            await speak(data?.news?.text);
            await speak(data?.ident?.text);
            try { localStorage.setItem(LAST_BULLETIN_KEY, String(Date.now())); } catch (_) {}
        } catch (_) {
            setStatusLine("Bulletin unavailable.");
        } finally {
            if (a) a.volume = prevVol;
            setBulletinBusy(false);
            // Iter 31.136 — clear announcer once readout finishes so the
            // pill swaps back to the track performer.
            setCurrentAnnouncer(null);
        }
    }, [speak, playJingle]);

    const incrementSongCounter = useCallback(async () => {
        let count = 0;
        try {
            count = parseInt(localStorage.getItem(COUNTER_KEY) || "0", 10) + 1;
            localStorage.setItem(COUNTER_KEY, String(count));
        } catch (_) { count = 0; }
        const every = songsPerBulletinFor(bulletinPoolSize);
        if (count > 0 && count % every === 0) {
            await playBulletin();
        }
    }, [playBulletin, bulletinPoolSize]);

    // Iter 31.26 — reaction-weighted next-track picker.
    // Weights: love 3× · like 1.5× · unrated 1× · dislike 0.1× (rare).
    // Pure client-side so we don't burn an API round-trip per song end.
    const pickNextIdx = useCallback((currentIdx) => {
        if (tracks.length === 0) return 0;
        if (tracks.length === 1) return 0;
        const weights = { love: 3.0, like: 1.5, dislike: 0.1 };
        // Build cumulative weights, skipping the current track so it
        // doesn't immediately repeat. (Dislike at 0.1× already keeps
        // those rare; current-track exclusion is purely UX.)
        const cumulative = [];
        let total = 0;
        for (let i = 0; i < tracks.length; i++) {
            if (i === currentIdx) continue;
            const w = weights[tracks[i].reaction] ?? 1.0;
            total += w;
            cumulative.push({ idx: i, cum: total });
        }
        if (total === 0) return (currentIdx + 1) % tracks.length;
        const r = Math.random() * total;
        for (const c of cumulative) if (r <= c.cum) return c.idx;
        return cumulative[cumulative.length - 1].idx;
    }, [tracks]);

    // Auto-advance on natural end + record event + counter trigger.
    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onEnded = async () => {
            if (currentTrack) {
                try { await api.post(`/radio/tracks/${currentTrack.id}/event`, { type: "played" }); } catch (_) {}
            }
            await incrementSongCounter();
            setTrackIdx((i) => pickNextIdx(i));
        };
        a.addEventListener("ended", onEnded);
        return () => a.removeEventListener("ended", onEnded);
    }, [currentTrack, pickNextIdx, incrementSongCounter]);

    // Kick playback when src loads.
    useEffect(() => {
        const a = audioRef.current;
        if (!a || !streamUrl) return;
        if (playing) {
            const tryPlay = () => a.play().catch(() => { /* user gesture */ });
            if (a.readyState >= 2) tryPlay();
            else a.addEventListener("canplay", tryPlay, { once: true });
        }
    }, [streamUrl, playing]);

    // Fail-safe bulletin every 2h.
    useEffect(() => {
        if (!playing) return undefined;
        const tick = () => {
            try {
                const last = parseInt(localStorage.getItem(LAST_BULLETIN_KEY) || "0", 10) || 0;
                if (Date.now() - last >= FALLBACK_HOURS_MAX * 60 * 60 * 1000) {
                    playBulletin();
                }
            } catch (_) { /* swallow */ }
        };
        const id = setInterval(tick, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [playing, playBulletin]);

    // ---- methods exposed via context ----
    const togglePlay = useCallback(async () => {
        const a = audioRef.current;
        if (!a) return;
        if (playing) {
            a.pause();
            setPlaying(false);
            return;
        }
        if (tracks.length === 0) {
            setStatusLine("No tracks. Drop audio in the Shredder.");
            return;
        }
        try {
            await a.play();
            setPlaying(true);
            setStatusLine("//ON AIR");
        } catch (_) {
            setStatusLine("Tap again — browser blocked autoplay.");
        }
    }, [playing, tracks.length]);

    // Iter 31.135 — skip() removed. Canon: the radio can be on or off,
    // it can't be skipped. Natural track end still advances via the
    // weighted picker; user can only Stop (toggle to off).

    // Iter 31.26 — set/clear reaction on the current (or any) track.
    // Optimistically mutates local state so the UI reflects instantly,
    // then persists to backend. The weighted shuffler picks it up on
    // the next track-end.
    const setReaction = useCallback(async (trackId, reaction) => {
        // reaction: "love" | "like" | "dislike" | null (toggle off)
        setTracks((prev) => prev.map((t) =>
            t.id === trackId ? { ...t, reaction } : t
        ));
        try {
            await api.put(`/radio/tracks/${trackId}/reaction`, { reaction });
        } catch (e) {
            // Revert on failure
            setStatusLine(`Reaction save failed: ${e?.message || e}`);
        }
    }, []);

    const value = {
        // state
        tracks, trackIdx, currentTrack,
        playing, muted, bulletinBusy, statusLine,
        bulletinPoolSize,
        // Iter 31.136 — performer-aware fields. currentAnnouncer is
        // the agent key reading a bulletin (or null when no bulletin
        // is on). currentPerformerKey derives from the playing track.
        currentAnnouncer,
        currentPerformerKey: currentTrack?.performer?.key || "twenty",
        // setters
        setMuted,
        // methods
        togglePlay, playBulletin, setReaction,
        // Iter 31.132 — expose the audio DOM element so PerformerOverlay
        // can hook a Web Audio analyser onto it for music-driven bopping.
        audioRef,
    };

    return (
        <RadioContext.Provider value={value}>
            {/* The <audio> element lives HERE — at the provider level,
                ABOVE all routes — so navigating between pages never
                unmounts it. Hidden from layout; the visible button +
                dropdown live in <RadioPlayer /> rendered inside Shell. */}
            <audio
                ref={audioRef}
                src={streamUrl || undefined}
                preload="auto"
                data-testid="radio-audio"
                style={{ display: "none" }}
            />
            {children}
        </RadioContext.Provider>
    );
}
